import { memo, useCallback, useMemo } from 'react'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { useTranslation } from '@crew/modules/i18n'
import { useGetContractUserCountQuery } from '@crew/apis/contract/contractApis'
import { useContractPurchaseUserPage } from './useContractPurchaseUserPage'
import { CrewNumberBoxField } from 'components/forms/crewNumberBoxField'
import { BillingCycle } from '@crew/enums/dist/app'
import { useWatch } from 'react-hook-form'

type RenderContractUserAmountProps = {
  billingCycle: BillingCycle | undefined
  totalAmount: number
}

// 契約中ユーザーの年額表示か月額表示を描画するコンポーネント
const RendercontractUserAmount = memo(
  (props: RenderContractUserAmountProps) => {
    const { t } = useTranslation()
    // 年間契約
    if (props.billingCycle === BillingCycle.Year) {
      return (
        <div className="text-blue-500">
          {props.totalAmount === 0
            ? t('label.free') // 0の場合は「無料」と表示する
            : t('label.priceAnnualText', {
                price: props.totalAmount.toLocaleString(),
              })}
        </div>
      )
    } else {
      // 月間契約
      return (
        <div className="text-blue-500">
          {props.totalAmount === 0
            ? t('label.free') // 0の場合は「無料」と表示する
            : t('label.priceMonthlyText', {
                price: props.totalAmount.toLocaleString(),
              })}
        </div>
      )
    }
  }
)

export const ContractPurchaseUserPage = memo(() => {
  const { data: getContractUserCountResult } = useGetContractUserCountQuery()

  const {
    control,
    updateContractUserCount,
    cancelUpdateContractUserCount,
    updateContractUserCountLoading,
  } = useContractPurchaseUserPage(getContractUserCountResult?.contractUserCount)

  const { t } = useTranslation()

  // 「変更する」ボタンクリック時のイベントハンドラ
  const handleChangeUserContractUserButtonClick = useCallback(() => {
    updateContractUserCount()
  }, [updateContractUserCount])

  // キャンセルボタンクリック時のイベントハンドラ
  const handleCancelButtonClick = useCallback(() => {
    cancelUpdateContractUserCount()
  }, [cancelUpdateContractUserCount])

  // numberBoxに現在入力中の契約ユーザ数。入力値の変更に追従して表示を変更するためuseWatchを使用する
  const inputedContractUsersCount = useWatch({
    control,
    name: 'contractUserCount',
  })

  // 現在の契約ユーザー数
  const currentContractUserCount = useMemo(() => {
    if (getContractUserCountResult) {
      return getContractUserCountResult.contractUserCount
    }

    return 0
  }, [getContractUserCountResult])

  // 1ユーザーあたりの年額単価
  const annualUnitPrice = useMemo(() => {
    if (getContractUserCountResult) {
      return getContractUserCountResult.annualUnitPrice
    }

    return 0
  }, [getContractUserCountResult])

  // 1ユーザーあたりの月額単価
  const monthlyUnitPrice = useMemo(() => {
    if (getContractUserCountResult) {
      return getContractUserCountResult.monthlyUnitPrice
    }

    return 0
  }, [getContractUserCountResult])

  // 請求サイクル
  const billingCycle = useMemo(() => {
    if (getContractUserCountResult) {
      return getContractUserCountResult.billingCycle
    }
  }, [getContractUserCountResult])

  // 現在の外部ユーザーオプションの合計額を返却する
  const currentContractUserAmount = useMemo(() => {
    // 年払い
    if (billingCycle === BillingCycle.Year) {
      // 単価×人数の年額
      return annualUnitPrice * currentContractUserCount
    } else {
      // 月払い
      // 単価×人数の月額
      return monthlyUnitPrice * currentContractUserCount
    }
  }, [
    annualUnitPrice,
    billingCycle,
    currentContractUserCount,
    monthlyUnitPrice,
  ])

  // 変更後の外部ユーザーオプションの合計額を返却する
  const changedContractUserAmount = useMemo(() => {
    // useWatchで監視しているinputedContracctUsersCountが、useFormのdefaultValues設定だと走らないので、初期描画時はundefinedになる
    // 初期描画時のフォームの値はどのみち現在の契約ユーザー数になるので、undefinedの場合はcurrentContractUserCountを使用する
    let userCount = inputedContractUsersCount ?? currentContractUserCount

    if (billingCycle === BillingCycle.Year) {
      // 年払い
      // 単価×人数の年額
      return annualUnitPrice * userCount
    } else {
      // 月払い
      // 単価×人数の月額
      return monthlyUnitPrice * userCount
    }
  }, [
    annualUnitPrice,
    billingCycle,
    currentContractUserCount,
    inputedContractUsersCount,
    monthlyUnitPrice,
  ])

  // データが取得できていない場合は何も表示しない
  if (!getContractUserCountResult) return

  return (
    <form>
      <div className="flex flex-col gap-y-2.5 mx-auto w-full max-w-2xl">
        <div className="py-2.5 w-full flex-col justify-start items-start gap-2.5 flex">
          {/** 契約ユーザー数を変更 */}
          <span className="text-md font-bold">
            {t('label.contractPurchaseUser.title')}
          </span>

          <div className="py-1.5 w-full flex-col justify-start items-start gap-y-2.5 flex">
            {/** ご契約中のユーザー数を変更します */}
            <span>{t('label.contractPurchaseUser.description')}</span>
            {/** 現時点で有効なユーザー数を下回ることは出来ません。 */}
            <span>{t('label.contractPurchaseUser.note')}</span>

            {/** 現在の契約数 */}
            <div className="flex-row flex gap-2.5 items-center w-full">
              <span className="crew-text-gray-4 flex w-20">
                {t('label.contractNow')}
              </span>
              <span className="crew-text-gray-4">
                {t('label.numberOfPeople', {
                  people: getContractUserCountResult.contractUserCount,
                })}
              </span>
              <span className="text-blue-500 ml-auto">
                <RendercontractUserAmount
                  billingCycle={billingCycle}
                  totalAmount={currentContractUserAmount}
                />
              </span>
            </div>
            {/** 変更後の契約数 */}
            <div className="flex-row flex gap-2.5 items-center w-full">
              <span className="crew-text-gray-4 flex w-20">
                {t('label.contractAfterChange')}
              </span>
              <span className="crew-text-gray-4">
                <div className="flex-row w-full justify-start items-center gap-2.5 inline-flex">
                  {/* ユーザー数変更用のラジオボタン */}
                  <CrewNumberBoxField
                    control={control}
                    id="contractUserCount"
                    name="contractUserCount"
                    step={1}
                    showLabel={false}
                    width={80}
                    min={0}
                    showSpinButtons={true}
                    defaultValue={currentContractUserCount}
                  />
                  <span>{t('label.people')}</span>
                </div>
              </span>
              <span className="text-blue-500 ml-auto">
                <RendercontractUserAmount
                  billingCycle={billingCycle}
                  totalAmount={changedContractUserAmount}
                />
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-row gap-x-2.5">
          <CrewButton
            text={t('action.change')}
            type="primary"
            onClick={handleChangeUserContractUserButtonClick}
            disabled={updateContractUserCountLoading}
          />
          <CrewButton
            text={t('action.cancel')}
            type="normal"
            stylingMode="outlined"
            onClick={handleCancelButtonClick}
          />
        </div>
      </div>
    </form>
  )
})
